import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { TLearningCurveInfoActionType } from './learning-curve.models';
import * as LearningCurveActions from './learning-curve.actions';
import { LearningCurveFacade } from './learning-curve.facade';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { TLearningCurveDTO } from '@monorepo/types';

@Injectable({ providedIn: 'root' })
export class LearningCurveEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private learningCurveFacade: LearningCurveFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  getLearningCurves$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLearningCurveInfoActionType.GetLearningCurveInfo),
      switchMap((action: LearningCurveActions.GetLearningCurvesInfo) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.fetchLearningCurves(action.fetchLearningCurvePayload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LearningCurveActions.GetLearningCurvesInfoSuccess(res)),
          catchError(() => {
            return of(new LearningCurveActions.GetLearningCurvesInfoFailure());
          }),
        ),
      ),
    ),
  );

  addEditLearningCurve = createEffect(() =>
    this.actions$.pipe(
      ofType(TLearningCurveInfoActionType.AddEditLearningCurve),
      switchMap((action: LearningCurveActions.AddEditLearningCurve) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        const updatedLearningCurves = [];
        updatedLearningCurves.push(action.learningCurves as TLearningCurveDTO);
        return this.plani8DataService
          .addUpdateLearningCurves({
            learning_curves: updatedLearningCurves,
          })
          .pipe(
            finalize(() => {
              this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
            }),
            map((res) => {
              if (action.addEditType === 'ADD') {
                this.learningCurveFacade
                  .getUserPaginationOption()
                  .pipe(take(1))
                  .subscribe((currUserPaginationOption) => {
                    this.learningCurveFacade.setUserPaginationOption({
                      ...currUserPaginationOption,
                      pageNo: 1,
                    });
                  });
              }
              this.appNotificationUtilClass.presentToast(
                'The changes in the learning curve would apply to the strip length from tomorrow.',
              );
              return new LearningCurveActions.GetLearningCurvesInfo(action.fetchLearningCurvePayload);
            }),
            catchError(() => of(new LearningCurveActions.AddEditLearningCurveFailure())),
          );
      }),
    ),
  );

  deleteLearningCurves = createEffect(() =>
    this.actions$.pipe(
      ofType(TLearningCurveInfoActionType.DeleteLearningCurves),
      switchMap((action: LearningCurveActions.DeleteLearningCurves) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.deleteLearningCurves(action.learningCurvesIds).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LearningCurveActions.GetLearningCurvesInfo(action.fetchLearningCurvePayload)),
          catchError(() => of(new LearningCurveActions.DeleteLearningCurvesFailure())),
        );
      }),
    ),
  );

  addLearningCurveDay = createEffect(() =>
    this.actions$.pipe(
      ofType(TLearningCurveInfoActionType.AddLearningCurveDay),
      switchMap((action: LearningCurveActions.AddLearningCurvesDay) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.addLearningCurveDay(action.currDays).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LearningCurveActions.GetLearningCurvesInfo(action.fetchLearningCurvePayload)),
          catchError(() => of(new LearningCurveActions.AddLearningCurvesDayFailure())),
        );
      }),
    ),
  );
}
